import { https } from "../http";
import store from "../store";
import { showError } from "../utils/index";
import {
  setLoading,
  setSaving,
  setBoards,
  setKanbanLoading,
  setKanban,
  moveCardLocal,
  setTemplates,
  setIssueCategories,
  setRequests,
  setCounts,
  setKpisLoading,
  setDashboardKpis,
  setStaffRequests,
  setStaffCounts,
  setSelectedRequestLoading,
  setSelectedRequest,
  patchRequestInLists,
  removeRequestFromLists,
  reorderRequests,
  setSettingsLoading,
  setSettingsSaving,
  setSettings,
} from "../store/reducers/serviceRequests";

const BASE = "/service-request";

const unwrap = (response) => {
  const body = response?.data;
  if (body && body.success === false) {
    throw new Error(body.message || "Request failed");
  }
  return body?.data;
};

const handleError = (error) => {
  showError(error?.response?.data?.message, error?.response?.status);
};

// ---------------------------------------------------------------- Issue Categories
const getIssueCategories = async (active) => {
  try {
    const response = await https().get(`${BASE}/issue-categories`, { params: active != null ? { active } : {} });
    store.dispatch(setIssueCategories(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const createIssueCategory = async (data, callback) => {
  try {
    await https().post(`${BASE}/issue-categories`, data);
    await getIssueCategories();
    callback?.();
  } catch (error) {
    handleError(error);
  }
};

const updateIssueCategory = async (id, data, callback) => {
  try {
    await https().patch(`${BASE}/issue-categories/${id}`, data);
    await getIssueCategories();
    callback?.();
  } catch (error) {
    handleError(error);
  }
};

const deleteIssueCategory = async (id, callback) => {
  try {
    await https().delete(`${BASE}/issue-categories/${id}`);
    await getIssueCategories();
    callback?.();
  } catch (error) {
    handleError(error);
  }
};

// ---------------------------------------------------------------- Boards
// A board is one general, tenant-wide kanban surface — not scoped to a
// facility or template. The backend lazily creates it the first time it's
// needed, so this always resolves to exactly one.
const getBoards = async () => {
  try {
    const response = await https().get(`${BASE}/boards`);
    store.dispatch(setBoards(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const getBoard = async (id) => {
  try {
    const response = await https().get(`${BASE}/boards/${id}`);
    return unwrap(response);
  } catch (error) {
    handleError(error);
    return null;
  }
};

const createBoard = async (data, callback) => {
  try {
    const response = await https().post(`${BASE}/boards`, data);
    await getBoards();
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

const updateBoard = async (id, data, callback) => {
  try {
    const response = await https().patch(`${BASE}/boards/${id}`, data);
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

const reorderBoardColumns = async (id, columns, callback) => {
  try {
    const response = await https().patch(`${BASE}/boards/${id}/columns/reorder`, columns);
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

const setBoardColumnRole = async (id, key, data, callback) => {
  try {
    const response = await https().patch(`${BASE}/boards/${id}/columns/${key}/role`, data);
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

// ---------------------------------------------------------------- Templates
// A template has no relationship to Facility — no filter to pass here.
const getTemplates = async () => {
  try {
    const response = await https().get(`${BASE}/templates`);
    store.dispatch(setTemplates(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const getTemplate = async (id) => {
  try {
    const response = await https().get(`${BASE}/templates/${id}`);
    return unwrap(response);
  } catch (error) {
    handleError(error);
    return null;
  }
};

const createTemplate = async (data, callback) => {
  try {
    const response = await https().post(`${BASE}/templates`, data);
    await getTemplates();
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

const updateTemplate = async (id, data, callback) => {
  try {
    const response = await https().patch(`${BASE}/templates/${id}`, data);
    await getTemplates();
    callback?.(unwrap(response));
  } catch (error) {
    handleError(error);
  }
};

const deleteTemplate = async (id, callback) => {
  try {
    await https().delete(`${BASE}/templates/${id}`);
    await getTemplates();
    callback?.();
  } catch (error) {
    handleError(error);
  }
};

// ---------------------------------------------------------------- Service Requests
// Returns the created request, or null when the POST failed (the error is
// already surfaced) — so a caller can await it and keep its own submit button
// spinning until the round trip actually finishes.
const createServiceRequest = async (data, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().post(BASE, data);
    store.dispatch(setSaving(false));
    const created = unwrap(response);
    callback?.(created);
    return created;
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
    return null;
  }
};

const getServiceRequests = async (params = {}, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    const response = await https().get(BASE, { params });
    const body = response?.data;
    store.dispatch(setRequests({ data: body?.data, total: body?.total }));
  } catch (error) {
    store.dispatch(setLoading(false));
    handleError(error);
  }
};

/**
 * The report builder applies its own facility / client / type / staff / date
 * filters, so it needs the whole set — not the `requests` slice, which only
 * holds whatever the backlog view last fetched (and is empty entirely while
 * the board view is mounted). Returns the rows instead of dispatching, so
 * opening the report drawer never clobbers the list behind it.
 *
 * Deliberately sends no `page`: the list endpoint only applies skip/limit
 * when `page` is present, so omitting it is what returns the whole set —
 * passing `page: 1` would silently cap the report at the default limit of 10.
 */
const fetchServiceRequestsForReport = async (params = {}) => {
  try {
    const response = await https().get(BASE, { params });
    return response?.data?.data || [];
  } catch (error) {
    handleError(error);
    return [];
  }
};

const getServiceRequestCounts = async (params = {}) => {
  try {
    const response = await https().get(`${BASE}/counts`, { params });
    store.dispatch(setCounts(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const getServiceRequestKanban = async (assignedTo) => {
  try {
    store.dispatch(setKanbanLoading(true));
    const response = await https().get(`${BASE}/kanban`, { params: assignedTo ? { assignedTo } : {} });
    store.dispatch(setKanban(unwrap(response)));
  } catch (error) {
    store.dispatch(setKanbanLoading(false));
    handleError(error);
  }
};

// showLoading=false keeps the KPI digits on screen while they refresh in the
// background (after a request is created, say) — only the first landing on
// the page should blank them out to skeletons.
const getDashboardKpis = async (showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setKpisLoading(true));
    const response = await https().get(`${BASE}/dashboard-kpis`);
    store.dispatch(setDashboardKpis(unwrap(response)));
  } catch (error) {
    store.dispatch(setKpisLoading(false));
    handleError(error);
  }
};

const getStaffMyRequests = async (tab) => {
  try {
    const response = await https().get(`${BASE}/staff/my-requests`, { params: { tab } });
    store.dispatch(setStaffRequests(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const getStaffMyRequestsCounts = async () => {
  try {
    const response = await https().get(`${BASE}/staff/my-requests/counts`);
    store.dispatch(setStaffCounts(unwrap(response)));
  } catch (error) {
    handleError(error);
  }
};

const getServiceRequestById = async (id) => {
  try {
    store.dispatch(setSelectedRequestLoading(true));
    const response = await https().get(`${BASE}/${id}`);
    store.dispatch(setSelectedRequest(unwrap(response)));
  } catch (error) {
    store.dispatch(setSelectedRequestLoading(false));
    handleError(error);
  }
};

// Returns the updated request, or null when the PATCH failed — so a caller
// chaining follow-up calls (status move, reassignment) can stop on failure
// instead of reporting a save that didn't happen.
const updateServiceRequest = async (id, data, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().patch(`${BASE}/${id}`, data);
    const updated = unwrap(response);
    store.dispatch(setSaving(false));
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
    return updated;
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
    return null;
  }
};

// Comments tab on the detail drawer. The response carries the whole
// populated request back (with the new comment's author resolved), so the
// thread re-renders without a follow-up fetch.
const addServiceRequestComment = async (id, text, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().post(`${BASE}/${id}/comments`, { text });
    const updated = unwrap(response);
    store.dispatch(setSaving(false));
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
    return updated;
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
    return null;
  }
};

// Kanban drag-drop and the backlog row's "Move" action both call this.
// Optimistically moves the card client-side; on failure the caller should
// refetch the kanban/list to roll back.
const moveServiceRequest = async (id, fromColumnKey, toColumnKey, callback) => {
  store.dispatch(moveCardLocal({ id, fromColumnKey, toColumnKey }));
  try {
    const response = await https().patch(`${BASE}/${id}/move`, { toColumnKey });
    const updated = unwrap(response);
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Backlog row reorder. Client-side only — see the reducer: the list's order
// isn't something the server stores, so this holds until the next fetch.
const reorderBacklogRequest = (id, beforeId) => {
  store.dispatch(reorderRequests({ id, beforeId: beforeId || null }));
};

const deleteServiceRequest = async (id, callback) => {
  try {
    await https().delete(`${BASE}/${id}`);
    store.dispatch(removeRequestFromLists(id));
    callback?.();
  } catch (error) {
    handleError(error);
  }
};

const approveServiceRequest = async (id, comment, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().post(`${BASE}/${id}/approve`, { comment });
    const updated = unwrap(response);
    store.dispatch(setSaving(false));
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
  }
};

const rejectServiceRequest = async (id, comment, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().post(`${BASE}/${id}/reject`, { comment });
    const updated = unwrap(response);
    store.dispatch(setSaving(false));
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
  }
};

const assignServiceRequest = async (id, assignedTo, callback) => {
  try {
    const response = await https().patch(`${BASE}/${id}/assign`, { assignedTo });
    const updated = unwrap(response);
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
    return updated;
  } catch (error) {
    handleError(error);
    return null;
  }
};

// Requester self-cancel — only the request's own submitter, only while still
// backlog/ready, only when the business's allowRequesterCancel setting is on
// (all enforced server-side; this just calls the endpoint and syncs state).
const cancelServiceRequest = async (id, reason, callback) => {
  try {
    store.dispatch(setSaving(true));
    const response = await https().post(`${BASE}/${id}/cancel`, reason ? { reason } : {});
    const updated = unwrap(response);
    store.dispatch(setSaving(false));
    store.dispatch(patchRequestInLists({ id, patch: updated }));
    callback?.(updated);
  } catch (error) {
    store.dispatch(setSaving(false));
    handleError(error);
  }
};

const downloadServiceRequestPdf = async (id, params = {}) => {
  try {
    const response = await https().get(`${BASE}/${id}/pdf`, { params, responseType: "blob" });
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ServiceRequest_${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error);
  }
};

// ---------------------------------------------------------------- Settings (General + Notifications)
const getServiceRequestSettings = async () => {
  try {
    store.dispatch(setSettingsLoading(true));
    const response = await https().get(`${BASE}/settings`);
    store.dispatch(setSettings(unwrap(response)));
  } catch (error) {
    store.dispatch(setSettingsLoading(false));
    handleError(error);
  }
};

const updateServiceRequestSettings = async (patch, callback) => {
  try {
    store.dispatch(setSettingsSaving(true));
    const response = await https().patch(`${BASE}/settings`, patch);
    const updated = unwrap(response);
    store.dispatch(setSettings(updated));
    store.dispatch(setSettingsSaving(false));
    callback?.(updated);
  } catch (error) {
    store.dispatch(setSettingsSaving(false));
    handleError(error);
  }
};

export {
  getIssueCategories,
  createIssueCategory,
  updateIssueCategory,
  deleteIssueCategory,
  getBoards,
  getBoard,
  createBoard,
  updateBoard,
  reorderBoardColumns,
  setBoardColumnRole,
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  createServiceRequest,
  getServiceRequests,
  fetchServiceRequestsForReport,
  getServiceRequestCounts,
  getServiceRequestKanban,
  getDashboardKpis,
  getStaffMyRequests,
  getStaffMyRequestsCounts,
  getServiceRequestById,
  updateServiceRequest,
  addServiceRequestComment,
  moveServiceRequest,
  deleteServiceRequest,
  reorderBacklogRequest,
  approveServiceRequest,
  rejectServiceRequest,
  assignServiceRequest,
  cancelServiceRequest,
  downloadServiceRequestPdf,
  getServiceRequestSettings,
  updateServiceRequestSettings,
};
